<template>
  <div class="order-container">
    <!-- 顶部导航 -->
    <div class="nav-bar">
      <div class="nav-content">
        <div class="logo">
          <span class="glow-text">云商城</span>
        </div>
        <div class="nav-menu">
          <el-menu mode="horizontal" default-active="3" class="nav-menu-inner">
            <el-menu-item index="1" @click="goToHome">首页</el-menu-item>
            <el-menu-item index="3">我的订单</el-menu-item>
            <el-menu-item index="4" @click="goToCart">购物车</el-menu-item>
          </el-menu>
        </div>
        <div class="user-info">
          <el-dropdown @command="handleCommand">
            <span class="el-dropdown-link">
              {{ userNickname || '用户' }}
              <el-icon class="el-icon--right"><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="home">返回首页</el-dropdown-item>
                <el-dropdown-item command="profile">个人资料</el-dropdown-item>
                <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="main-content">
      <!-- 页面标题 -->
      <div class="page-header glassmorphism">
        <div class="page-header-left">
          <el-icon :size="28" color="#537ce5"><List /></el-icon>
          <div>
            <h1>我的订单</h1>
            <span class="header-tip">共 {{ orderList.length }} 个订单</span>
          </div>
        </div>
        <button class="btn-back" @click="goToHome">
          <el-icon><ArrowLeft /></el-icon>
          继续购物
        </button>
      </div>

      <!-- 订单状态筛选 -->
      <div class="order-tabs-wrapper glassmorphism">
        <el-tabs v-model="currentStatus" @tab-change="switchStatus" class="order-tabs">
          <el-tab-pane label="全部订单" name="all">
            <template #label>
              <span class="tab-label">
                全部订单
                <span v-if="getStatusCount('all') > 0" class="tab-count">({{ getStatusCount('all') }})</span>
              </span>
            </template>
          </el-tab-pane>
          <el-tab-pane label="待支付" name="0">
            <template #label>
              <span class="tab-label">
                <el-icon><Clock /></el-icon>
                待支付
                <span v-if="getStatusCount(0) > 0" class="tab-count">({{ getStatusCount(0) }})</span>
              </span>
            </template>
          </el-tab-pane>
          <el-tab-pane label="已支付" name="1">
            <template #label>
              <span class="tab-label">
                <el-icon><SuccessFilled /></el-icon>
                已支付
                <span v-if="getStatusCount(1) > 0" class="tab-count">({{ getStatusCount(1) }})</span>
              </span>
            </template>
          </el-tab-pane>
          <el-tab-pane label="已取消" name="2">
            <template #label>
              <span class="tab-label">
                <el-icon><CircleClose /></el-icon>
                已取消
                <span v-if="getStatusCount(2) > 0" class="tab-count">({{ getStatusCount(2) }})</span>
              </span>
            </template>
          </el-tab-pane>
        </el-tabs>
      </div>

      <!-- 订单列表 -->
      <template v-if="filteredOrders.length > 0">
        <div class="order-list">
          <div v-for="order in filteredOrders" :key="order.id" class="order-card glassmorphism">
            <!-- 订单头部 -->
            <div class="order-header">
              <div class="order-header-left">
                <span class="order-no">订单号：{{ order.orderNo }}</span>
                <span class="order-time">{{ order.createTime }}</span>
              </div>
              <div class="order-header-right">
                <el-tag
                  :type="getStatusTagType(order.status)"
                  effect="dark"
                  size="large"
                >
                  {{ getStatusLabel(order.status) }}
                </el-tag>
              </div>
            </div>

            <!-- 订单商品列表 -->
            <div class="order-items">
              <div v-for="item in order.items" :key="item.id" class="order-item">
                <div class="oi-image">
                  <el-image :src="getProductImage(item.picUrl)" fit="cover" class="oi-img">
                    <template #error>
                      <div class="image-placeholder">
                        <el-icon :size="24" color="#ccc"><Goods /></el-icon>
                      </div>
                    </template>
                  </el-image>
                </div>
                <div class="oi-info">
                  <h4 class="oi-title">{{ item.title || item.name }}</h4>
                  <span class="oi-category" v-if="item.category">{{ item.category }}</span>
                </div>
                <div class="oi-price">¥{{ item.price }}</div>
                <div class="oi-quantity">x{{ item.quantity }}</div>
                <div class="oi-subtotal">¥{{ (item.price * item.quantity).toFixed(2) }}</div>
              </div>
            </div>

            <!-- 订单底部 -->
            <div class="order-footer">
              <div class="order-total">
                共 <strong>{{ order.totalQuantity }}</strong> 件商品，
                合计：<span class="total-amount">¥{{ order.totalAmount }}</span>
              </div>
              <div class="order-actions">
                <template v-if="order.status === 0">
                  <button class="btn-action btn-pay" @click="handlePayOrder(order)">
                    <el-icon><CreditCard /></el-icon>
                    去支付
                  </button>
                  <button class="btn-action btn-cancel-order" @click="cancelOrder(order)">取消订单</button>
                </template>
                <template v-if="order.status === 1">
                  <el-tag type="success" effect="dark" size="large">已完成</el-tag>
                </template>
                <template v-if="order.status === 2">
                  <el-tag type="info" effect="dark" size="large">已关闭</el-tag>
                  <button class="btn-action btn-rebuy" @click="rebuy(order)">再次购买</button>
                </template>
                <button class="btn-action btn-delete-order" @click="deleteOrder(order)">
                  <el-icon><Delete /></el-icon>
                  删除
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- 空状态 -->
      <template v-else>
        <div class="empty-order glassmorphism">
          <div class="empty-icon-wrapper">
            <el-icon :size="80" color="#d0d0d0"><List /></el-icon>
          </div>
          <h2>{{ currentStatus === 'all' ? '暂无订单' : '暂无该状态的订单' }}</h2>
          <p>快去下单体验吧！</p>
          <button class="btn-go-shopping" @click="goToHome">
            <el-icon><ShoppingBag /></el-icon>
            去逛逛
          </button>
        </div>
      </template>
    </div>

    <!-- 底部 -->
    <div class="footer">
      <div class="footer-content">
        <div class="footer-section">
          <h4>关于我们</h4>
          <p>云商城是一个基于微服务架构的现代化交易平台。</p>
        </div>
        <div class="footer-section">
          <h4>快速链接</h4>
          <ul>
            <li><a href="#">帮助中心</a></li>
            <li><a href="#">联系我们</a></li>
            <li><a href="#">服务条款</a></li>
          </ul>
        </div>
        <div class="footer-section">
          <h4>联系方式</h4>
          <p>Email: support@cloudshop.com</p>
          <p>Phone: 400-800-8888</p>
        </div>
      </div>
      <div class="footer-bottom">
        <p>&copy; 2026 云商城 - 微服务交易平台. All rights reserved.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElNotification, ElMessageBox } from 'element-plus'
import {
  ArrowDown, ArrowLeft, List, ShoppingBag, Goods, Delete,
  CreditCard, CircleCheck, RefreshRight, Clock, Checked,
  Van, SuccessFilled, CircleClose
} from '@element-plus/icons-vue'
import { orderService, getCurrentUserId } from '../api/order'

const router = useRouter()
const userNickname = ref('')
const orderList = ref([])
const currentStatus = ref('all')
const defaultImage = 'https://via.placeholder.com/300x300?text=No+Image'

/** 获取商品图片 */
const getProductImage = (picUrl) => picUrl || defaultImage

/** 筛选后的订单 */
const filteredOrders = computed(() => {
  if (currentStatus.value === 'all') return orderList.value
  const statusNum = parseInt(currentStatus.value)
  return orderList.value.filter(o => o.status === statusNum)
})

/** 获取指定状态的订单数 */
const getStatusCount = (status) => {
  if (status === 'all') return orderList.value.length
  const statusNum = typeof status === 'string' ? parseInt(status) : status
  return orderList.value.filter(o => o.status === statusNum).length
}

/** 切换状态筛选 */
const switchStatus = (status) => {
  currentStatus.value = status
}

/** 状态中文标签（后端：0=待支付，1=已支付，2=已取消） */
const getStatusLabel = (status) => {
  const map = { 0: '待支付', 1: '已支付', 2: '已取消' }
  return map[status] ?? status
}

/** 状态标签类型（后端：0=待支付/danger，1=已支付/success，2=已取消/info） */
const getStatusTagType = (status) => {
  const map = { 0: 'danger', 1: 'success', 2: 'info' }
  return map[status] ?? ''
}

/** 生成订单号 */
const generateOrderNo = () => {
  const now = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
  const rand = Math.floor(Math.random() * 10000).toString().padStart(4, '0')
  return `OD${ts}${rand}`
}

/** 格式化时间 */
const formatTime = (date) => {
  const d = date || new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

/** 支付订单 — 跳转到支付页面 */
const handlePayOrder = async (order) => {
  router.push({ path: '/payment', query: { orderId: order.id } })
}

/** 取消订单 — 调用后端 /order/cancel/{id} */
const cancelOrder = async (order) => {
  try {
    await ElMessageBox.confirm(
      `确定取消订单 ${order.orderNo || order.id} 吗？`,
      '取消订单',
      { confirmButtonText: '确定取消', cancelButtonText: '再想想', type: 'warning' }
    )
    
    // 调用后端 API
    try {
      await orderService.cancelOrder(order.id)
      // 刷新列表
      await loadOrders()
      ElNotification({ title: '已取消', message: `订单已成功取消`, type: 'info', duration: 2000 })
    } catch (error) {
      console.error('取消订单失败:', error)
      ElMessage.error('取消订单失败，请稍后重试')
    }
  } catch { /* 用户取消 */ }
}

/** 确认收货 */
const confirmReceive = async (order) => {
  try {
    await ElMessageBox.confirm(
      `确认已收到订单 ${order.orderNo} 的商品吗？`,
      '确认收货',
      { confirmButtonText: '确认收货', cancelButtonText: '取消', type: 'info' }
    )
    order.status = 'completed'
    saveOrders()
    ElNotification({ title: '已确认收货', message: '感谢您的购买！', type: 'success', duration: 2000 })
  } catch { /* cancelled */ }
}

/** 查看物流 */
const viewLogistics = (order) => {
  ElMessage.info(`物流跟踪功能开发中... 订单号：${order.orderNo}`)
}

/** 再次购买 — 将订单商品重新加入购物车 */
const rebuy = (order) => {
  let cart = []
  try {
    cart = JSON.parse(localStorage.getItem('cart') || '[]')
  } catch { cart = [] }

  for (const item of order.items) {
    const existing = cart.find(c => c.id === item.id)
    if (existing) {
      existing.quantity += item.quantity
    } else {
      cart.push({ ...item, checked: false })
    }
  }

  localStorage.setItem('cart', JSON.stringify(cart))
  ElNotification({ title: '已加入购物车', message: `${order.items.length} 件商品已加入购物车`, type: 'success', duration: 1800 })
}

/** 删除订单 */
const deleteOrder = async (order) => {
  try {
    await ElMessageBox.confirm(
      `确定删除订单 ${order.orderNo} 吗？删除后不可恢复。`,
      '删除订单',
      { confirmButtonText: '删除', cancelButtonText: '取消', type: 'warning' }
    )
    orderList.value = orderList.value.filter(o => o.id !== order.id)
    saveOrders()
    ElNotification({ title: '已删除', message: `订单 ${order.orderNo} 已删除`, type: 'success', duration: 1500 })
  } catch { /* cancelled */ }
}

/** 保存订单到 localStorage */
const saveOrders = () => {
  localStorage.setItem('orders', JSON.stringify(orderList.value))
}

/** 加载订单 — 调用后端 /order/list/{userId} */
const loadOrders = async () => {
  const userId = getCurrentUserId()
  if (!userId) {
    ElMessage.warning('请先登录')
    router.push('/login')
    return
  }

  try {
    const res = await orderService.getOrderList(userId)
    const data = res.data?.data || res.data?.records || res.data
    
    if (Array.isArray(data)) {
      orderList.value = data.map(order => ({
        id: order.id,
        orderNo: order.orderNo || `OD${order.id}`,
        createTime: order.createTime || order.orderTime || formatTime(new Date()),
        status: order.status ?? 0,
        totalAmount: order.totalAmount || order.totalPrice || 0,
        totalQuantity: order.totalQuantity || order.count || 1,
        items: order.items || order.orderItems || [{
          id: order.productId,
          productId: order.productId,
          title: order.productName || '商品',
          name: order.productName,
          price: order.totalAmount || 0,
          quantity: order.count || 1,
          picUrl: order.picUrl || '',
        }]
      }))
    } else {
      orderList.value = []
    }
  } catch (error) {
    console.error('加载订单失败:', error)
    ElMessage.error('加载订单失败，请稍后重试')
    orderList.value = []
  }
}

/** 导航 */
const goToHome = () => router.push('/home')
const goToCart = () => router.push('/cart')

const handleCommand = (command) => {
  if (command === 'logout') {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    ElNotification({ title: '再见', message: '已成功退出登录', type: 'info', duration: 1800 })
    router.push('/login')
  } else if (command === 'home') {
    goToHome()
  } else if (command === 'profile') {
    router.push('/profile')
  }
}

onMounted(() => {
  const userStr = localStorage.getItem('user')
  if (userStr) {
    try {
      const user = JSON.parse(userStr)
      userNickname.value = user.nickname || user.username || '用户'
    } catch (e) { console.error('解析用户信息失败:', e) }
  }
  loadOrders()
})
</script>

<style scoped>
.order-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #232a4d 0%, #1a1f3a 50%, #2d1b3d 100%);
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}

/* ========== 导航栏 ========== */
.nav-bar {
  background: linear-gradient(135deg, #232a4d 0%, #4b2067 100%);
  box-shadow: 0 2px 8px #0000150d;
  position: sticky;
  top: 0;
  z-index: 1000;
  backdrop-filter: blur(10px);
}

.nav-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo { font-size: 24px; font-weight: bold; margin-right: 40px; min-width: 120px; }
.glow-text { color: #fff; text-shadow: 0 0 8px #a3a3ff, 0 0 16px #4b2067; }
.nav-menu { flex: 1; }
.nav-menu-inner { background: transparent !important; border-bottom: none !important; }
.nav-menu-inner :deep(.el-menu-item) { color: #fff !important; background: transparent !important; font-size: 0.95rem; }
.nav-menu-inner :deep(.el-menu-item:hover) { background: rgba(255,255,255,0.1) !important; }
.nav-menu-inner :deep(.el-menu-item.is-active) { border-bottom-color: #a3a3ff !important; }
.user-info { margin-left: auto; color: #fff; font-size: 14px; }
.user-info :deep(.el-dropdown-link) { color: #fff; cursor: pointer; display: flex; align-items: center; gap: 8px; }
.user-info :deep(.el-dropdown-link:hover) { color: #a3a3ff; }

/* ========== 主内容区 ========== */
.main-content {
  flex: 1;
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
  padding: 24px 16px 40px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.glassmorphism {
  background: rgba(255,255,255,0.08);
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.3);
  color: #fff;
}

/* ========== 页面标题 ========== */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px;
}

.page-header-left { display: flex; align-items: center; gap: 14px; }
.page-header-left h1 { font-size: 1.3rem; font-weight: 700; color: rgba(255,255,255,0.95); margin: 0; }
.header-tip { font-size: 0.85rem; color: rgba(255,255,255,0.6); }

.btn-back {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 20px; border: none; border-radius: 10px;
  background: linear-gradient(135deg, #232a4d 0%, #4b2067 100%);
  color: #fff; font-size: 0.9rem; font-weight: 600;
  cursor: pointer; transition: all 0.3s ease;
}
.btn-back:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(35,42,77,0.35); }

/* ========== 状态筛选 ========== */
.order-tabs-wrapper {
  padding: 16px 20px;
  margin-bottom: 0;
}

.order-tabs :deep(.el-tabs__header) {
  margin: 0;
  border-bottom: 2px solid rgba(255,255,255,0.1);
}

.order-tabs :deep(.el-tabs__nav-wrap::after) {
  display: none;
}

.order-tabs :deep(.el-tabs__item) {
  color: rgba(255,255,255,0.7);
  font-size: 0.95rem;
  font-weight: 500;
  padding: 0 24px;
  height: 44px;
  line-height: 44px;
}

.order-tabs :deep(.el-tabs__item:hover) {
  color: #a3a3ff;
}

.order-tabs :deep(.el-tabs__item.is-active) {
  color: #a3a3ff;
  font-weight: 600;
}

.order-tabs :deep(.el-tabs__active-bar) {
  background: linear-gradient(90deg, #a3a3ff 0%, #8b5fc9 100%);
  height: 3px;
}

.tab-label {
  display: flex;
  align-items: center;
  gap: 6px;
}

.tab-count {
  font-size: 0.8rem;
  color: rgba(255,255,255,0.5);
}

.order-tabs :deep(.el-tabs__item.is-active) .tab-count {
  color: #a3a3ff;
}

/* ========== 订单列表 ========== */
.order-list { display: flex; flex-direction: column; gap: 16px; }

.order-card {
  padding: 0;
  overflow: hidden;
  transition: all 0.3s ease;
}
.order-card:hover { box-shadow: 0 8px 28px rgba(163,163,255,0.2); transform: translateY(-2px); }

.order-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  background: rgba(163,163,255,0.08);
  border-bottom: 1px solid rgba(255,255,255,0.08);
}

.order-header-left { display: flex; align-items: center; gap: 20px; }
.order-no { font-size: 0.9rem; font-weight: 600; color: rgba(255,255,255,0.95); }
.order-time { font-size: 0.8rem; color: rgba(255,255,255,0.5); }

/* ========== 订单商品 ========== */
.order-items { padding: 12px 24px; }

.order-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}
.order-item:last-child { border-bottom: none; }

.oi-image { flex-shrink: 0; width: 60px; height: 60px; border-radius: 10px; overflow: hidden; }
.oi-img { width: 60px; height: 60px; }
.image-placeholder { width: 60px; height: 60px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.05); }

.oi-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.oi-title { font-size: 0.95rem; font-weight: 600; color: rgba(255,255,255,0.9); margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.oi-category { font-size: 0.75rem; color: rgba(255,255,255,0.5); }

.oi-price { flex-shrink: 0; width: 80px; text-align: center; font-size: 0.9rem; color: rgba(255,255,255,0.8); font-weight: 500; }
.oi-quantity { flex-shrink: 0; width: 50px; text-align: center; font-size: 0.85rem; color: rgba(255,255,255,0.6); }
.oi-subtotal { flex-shrink: 0; width: 90px; text-align: right; font-size: 0.95rem; color: #ff7f7f; font-weight: 600; }

/* ========== 订单底部 ========== */
.order-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  background: rgba(255,255,255,0.03);
  border-top: 1px solid rgba(255,255,255,0.08);
}

.order-total { font-size: 0.9rem; color: rgba(255,255,255,0.7); }
.order-total strong { color: rgba(255,255,255,0.95); }
.total-amount { font-size: 1.15rem; font-weight: 800; color: #ff7f7f; }

.order-actions { display: flex; gap: 8px; flex-wrap: wrap; }

.btn-action {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;
  border: 1px solid transparent;
}

.btn-pay {
  background: linear-gradient(135deg, #4e9aff 0%, #3b82f6 100%);
  color: #fff; border: none;
  font-weight: 600;
  padding: 8px 20px;
}
.btn-pay:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(59,130,246,0.4); }

.btn-confirm {
  background: linear-gradient(135deg, #43b581 0%, #2e8b57 100%);
  color: #fff; border: none;
}
.btn-confirm:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(46,139,87,0.35); }

.btn-rebuy {
  background: linear-gradient(135deg, #537ce5 0%, #8b5fc9 100%);
  color: #fff; border: none;
}
.btn-rebuy:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(83,124,229,0.35); }

.btn-info-outline {
  background: transparent;
  color: rgba(255,255,255,0.6);
  border: 1px solid rgba(255,255,255,0.2);
}
.btn-info-outline:hover { border-color: #a3a3ff; color: #a3a3ff; }
.btn-info-outline:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-cancel-order {
  background: transparent;
  color: rgba(255,255,255,0.5);
  border: 1px solid rgba(255,255,255,0.15);
}
.btn-cancel-order:hover { border-color: #ff7f7f; color: #ff7f7f; }

.btn-delete-order {
  background: transparent;
  color: rgba(255,255,255,0.4);
  border: 1px solid rgba(255,255,255,0.1);
}
.btn-delete-order:hover { background: rgba(255,127,127,0.1); border-color: #ff7f7f; color: #ff7f7f; }

/* ========== 空状态 ========== */
.empty-order {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  gap: 12px;
}

.empty-icon-wrapper {
  width: 120px; height: 120px; border-radius: 50%;
  background: rgba(163,163,255,0.1);
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 8px;
}

.empty-order h2 { font-size: 1.25rem; color: rgba(255,255,255,0.9); margin: 0; }
.empty-order p { font-size: 0.95rem; color: rgba(255,255,255,0.5); margin: 0; }

.btn-go-shopping {
  display: flex; align-items: center; gap: 6px;
  padding: 12px 32px; border: none; border-radius: 12px;
  background: linear-gradient(135deg, #537ce5 0%, #8b5fc9 100%);
  color: #fff; font-size: 1rem; font-weight: 600;
  cursor: pointer; transition: all 0.3s ease; margin-top: 8px;
}
.btn-go-shopping:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(83,124,229,0.4); }

/* ========== 底部 ========== */
.footer { background: #232a4d; color: #fff; margin-top: auto; padding: 30px 20px 15px; }
.footer-content { max-width: 1400px; margin: 0 auto; display: grid; grid-template-columns: repeat(auto-fit, minmax(250px,1fr)); gap: 30px; margin-bottom: 25px; }
.footer-section h4 { margin-bottom: 16px; font-size: 1.1rem; }
.footer-section p { font-size: 0.9rem; opacity: 0.8; margin-bottom: 8px; }
.footer-section ul { list-style: none; padding: 0; }
.footer-section li { margin-bottom: 8px; }
.footer-section a { color: #a3a3ff; text-decoration: none; transition: color 0.3s; }
.footer-section a:hover { color: #fff; }
.footer-bottom { border-top: 1px solid #4b206730; padding-top: 20px; text-align: center; font-size: 0.85rem; opacity: 0.7; }

/* ========== 响应式 ========== */
@media (max-width: 768px) {
  .order-header { flex-direction: column; align-items: flex-start; gap: 8px; }
  .order-header-left { flex-direction: column; gap: 4px; }
  .order-item { flex-wrap: wrap; gap: 8px; }
  .oi-info { width: calc(100% - 80px); flex: none; }
  .oi-price, .oi-quantity, .oi-subtotal { width: auto; }
  .order-footer { flex-direction: column; align-items: flex-start; gap: 12px; }
  .order-actions { width: 100%; justify-content: flex-end; }
  .page-header { flex-direction: column; gap: 12px; align-items: flex-start; }
  .order-tabs { gap: 4px; padding: 10px 14px; }
  .tab-btn { padding: 6px 12px; font-size: 0.82rem; }
  .nav-content { flex-wrap: wrap; gap: 12px; }
  .nav-menu { width: 100%; }
  .user-info { width: 100%; margin-left: 0; }
}
</style>
